import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '@/contexts/AuthContext';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuthState } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDescription || errorParam);
      return;
    }

    // Listen for Amplify's OAuth sign-in completion.
    // When signInWithRedirect redirects back here with ?code=...,
    // Amplify automatically exchanges the code for tokens and fires
    // a Hub event when complete.
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          // OAuth sign-in succeeded — Amplify has tokens now
          try {
            await checkAuthState();
            navigate('/', { replace: true });
          } catch (err) {
            console.error('Post-OAuth auth check failed:', err);
            setError(err.message || 'Authentication failed');
          }
          break;

        case 'signInWithRedirect_failure':
          console.error('OAuth sign-in failure:', payload.data);
          setError(payload.data?.message || 'Google sign in failed');
          break;

        default:
          break;
      }
    });

    // Also check if Amplify already processed the code (race condition guard).
    // If the Hub event already fired before our listener registered,
    // the user will already have a valid session.
    const checkExistingSession = async () => {
      try {
        // Small delay to let Amplify process the code exchange
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const user = await getCurrentUser();
        if (user) {
          const session = await fetchAuthSession();
          if (session.tokens) {
            await checkAuthState();
            navigate('/', { replace: true });
          }
        }
      } catch {
        // Not yet authenticated — Hub listener will handle it
      }
    };

    checkExistingSession();

    return () => unsubscribe();
  }, [searchParams, checkAuthState, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-4 text-center">
          <div className="rounded-full h-16 w-16 bg-destructive/10 mx-auto flex items-center justify-center">
            <svg
              className="h-8 w-8 text-destructive"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold">Authentication Failed</h1>
          <p className="text-muted-foreground">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="text-primary underline hover:no-underline"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="space-y-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
